// Csapat (team) create/edit — jobb oldali beúszó FormSheet (a department/position form-sheet testvére).
// Egy komponens KÉT módban: `team == null` → LÉTREHOZÁS (POST), különben SZERKESZTÉS (PATCH If-Match/OCC).
// A név/leírás KÉTNYELVŰ (nameHu/nameEn + descriptionHu/En). A `color` egy NEVESÍTETT szín-TOKEN a
// MORGENS-palettából (team-colors.ts) — a választó vizuális swatch-sor (NINCS hardcode hex a UI-ban, a hex a
// paletta egyetlen forrásában él). A SZÍN ITT él: a részlegnek nincs színe, a beosztás pedig a csapatáét
// örökli — ez a rendszer egyetlen szervezeti szín-forrása.
// A `departmentId` KÖTELEZŐ (csapat nem létezhet részleg nélkül); a választó a részleg-lista referencia-
// lekérdezéséből töltődik. A `slug` GENERÁLT (nem form-mező; a szerver a nameEn-ből).
// A validálás forrás-igazsága a @atlas/contracts TeamWriteBodySchema (a features/org-structure/form.ts
// resolver-én át); a wire-body-t a `toTeamBody` normalizálja.
import { type OrgTeamRowSchema } from '@atlas/contracts';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { BilingualField } from '@/components/shared/bilingual-field';
import { ComboboxField, SelectField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { SlugHint } from '@/components/shared/slug-hint';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { employeeSearch } from '@/features/employees/search';
import { departmentKeys, positionKeys, teamKeys } from '@/features/org-structure/api';
import {
  buildTeamResolver,
  EMPTY_TEAM_FORM,
  toTeamBody,
  type TeamFormValues,
} from '@/features/org-structure/form';
import { api } from '@/lib/api';
import { formatName } from '@/lib/format-name';
import { teamColor, TEAM_COLOR_TOKENS } from '@/lib/team-colors';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { cn } from '@/lib/utils';

type TeamRow = z.infer<typeof OrgTeamRowSchema>;

/** Szín-swatch választó (RHF-be kötve) — a paletta tokenjeit tömör szín-pöttyként kínálja; az aktív gyűrűt kap.
 *  A háttér a token feloldott `bg`-je (teamColor) — NINCS hardcode hex, a paletta a forrás. */
function ColorSwatchField({ label }: { label: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <FormField<TeamFormValues>
      name="color"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
            {TEAM_COLOR_TOKENS.map((token) => {
              const active = field.value === token;
              const { bg } = teamColor(token);
              const name = t(`orgStructure.colorLabel.${token}`, { defaultValue: token });
              return (
                <button
                  key={token}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={name}
                  title={name}
                  onClick={() => field.onChange(token)}
                  className={cn(
                    'ease-atlas size-7 rounded-full border transition-transform duration-150 hover:scale-110',
                    'focus-visible:ring-ring focus:outline-none focus-visible:ring-2',
                    active
                      ? 'ring-foreground ring-offset-background border-transparent ring-2 ring-offset-2'
                      : 'border-border',
                  )}
                  style={{ backgroundColor: bg }}
                />
              );
            })}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export interface TeamFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő csapat (hordozza az OCC `version`-t + az előtöltést). */
  team: TeamRow | null;
}

export function TeamFormSheet({ open, onOpenChange, team }: TeamFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEdit = team !== null;
  const isEn = i18n.language.toLowerCase().startsWith('en');

  // Részleg-opciók a lista referencia-lekérdezéséből (nagy limit — a belső részleg-szám kicsi, egy lap elég).
  const deptQ = api.orgDepartments.useQuery(departmentKeys.refs(), {
    query: { limit: 200, sort: 'name', order: 'asc' },
  });
  const departments = deptQ.data?.status === 200 ? deptQ.data.body.departments : [];
  const departmentOptions = departments.map((d) => ({
    value: d.id,
    label: isEn ? d.nameEn : d.nameHu,
  }));

  // a csapatvezető a belső címtár KÖZÖS keresőjével (mint az eszköz birtokosa)
  const searchEmployees = employeeSearch(isEn ? 'en' : 'hu');
  const leadOption =
    team?.lead != null
      ? {
          id: team.lead.id,
          label: formatName(
            {
              givenName: team.lead.givenName,
              familyName: team.lead.familyName,
              displayName: team.lead.displayName,
            },
            i18n.language,
          ),
        }
      : null;

  const form = useForm<TeamFormValues>({
    resolver: buildTeamResolver(t),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_TEAM_FORM,
  });
  const { reset, handleSubmit, setError } = form;

  useEffect(() => {
    if (team) {
      reset({
        nameHu: team.nameHu,
        nameEn: team.nameEn,
        color: team.color || 'gray',
        departmentId: team.departmentId ?? '',
        descriptionHu: team.descriptionHu ?? '',
        descriptionEn: team.descriptionEn ?? '',
        leadIdentityId: team.leadIdentityId ?? '',
      });
    } else {
      reset(EMPTY_TEAM_FORM);
    }
  }, [team, reset]);

  const mutation = useEntityMutation<TeamFormValues>({
    mutationFn: (values) => {
      const body = toTeamBody(values);
      return isEdit
        ? api.teamUpdate.mutation({
            params: { id: team.id },
            body,
            headers: { 'if-match': String(team.version) },
          })
        : api.teamCreate.mutation({ body });
    },
    // A csapat NEVE és SZÍNE szerver-oldalon feloldott a beosztás-sorokon (teamName/teamColor) és a
    // részleg-sorokon (teams[]) → egy átnevezés/szín-váltás után MINDKÉT másik lista elavul.
    invalidateKeys: [
      teamKeys.lists(),
      teamKeys.refs(),
      departmentKeys.lists(),
      positionKeys.lists(),
    ],
    onFieldErrors: (fieldName, messages) => {
      if (fieldName in EMPTY_TEAM_FORM) {
        setError(fieldName as keyof TeamFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit
        ? t('toast:org_structure.teams.updated')
        : t('toast:org_structure.teams.created'),
      conflict: t('toast:org_structure.teams.conflict'),
      forbidden: t('toast:org_structure.teams.forbidden'),
      validation: t('toast:org_structure.teams.validation'),
      error: t('toast:org_structure.teams.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_TEAM_FORM);
        onOpenChange(o);
      }}
      title={isEdit ? t('orgStructure.teams.editTitle') : t('orgStructure.teams.createTitle')}
      description={isEdit ? <SlugHint slug={team.slug} /> : t('orgStructure.teams.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('orgStructure.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('orgStructure.saving') : t('orgStructure.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <ColorSwatchField label={t('orgStructure.fields.color')} />
        <BilingualField<TeamFormValues>
          variant="input"
          label={t('orgStructure.fields.name')}
          nameHu="nameHu"
          nameEn="nameEn"
          required
        />
        <SelectField<TeamFormValues>
          name="departmentId"
          label={t('orgStructure.fields.department')}
          placeholder={t('orgStructure.teams.selectDepartment')}
          options={departmentOptions}
          required
        />
        <ComboboxField<TeamFormValues>
          name="leadIdentityId"
          label={t('orgStructure.fields.lead')}
          hint={t('orgStructure.teams.leadHint')}
          search={searchEmployees}
          initialOption={leadOption}
          placeholder={t('orgStructure.teams.leadPlaceholder')}
        />
        <BilingualField<TeamFormValues>
          variant="textarea"
          label={t('orgStructure.fields.description')}
          nameHu="descriptionHu"
          nameEn="descriptionEn"
          rows={2}
        />
      </Form>
    </FormSheet>
  );
}
